import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../../../styles/ui.css';
import './NewRequestWizard.css';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DOC_LABELS = {
  PROGRESS_REPORT: 'Progress Report',
  FINAL_REPORT: 'Final Report',
  EXT_REQUEST_LETTER: 'Request Letter',
  EXT_PROGRESS_REPORT_LAO: 'Progress Report (Lao)',
  EXT_PROGRESS_REPORT_ENG: 'Progress Report (Eng)',
  EXT_CURRENT_APPROVAL: 'Current Approval',
  EXT_FEE: 'Payment',
  AMD_REQUEST_LETTER: 'Request Letter',
  AMD_REVISED_DOCS_LAO: 'Revised Documents (Lao)',
  AMD_REVISED_DOCS_ENG: 'Revised Documents (Eng)',
  AMD_CURRENT_APPROVAL: 'Current Approval',
  AMD_FEE: 'Payment',
};

function normalizeKind(v) {
  const k = String(v || '').toUpperCase();
  return ['EXTENSION', 'AMENDMENT', 'PROGRESS', 'FINAL'].includes(k) ? k : 'EXTENSION';
}

export default function NewRequestWizard() {
  const { proposalId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const kind = normalizeKind(sp.get('kind'));
  const isSingle = kind === 'PROGRESS' || kind === 'FINAL';

  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [proposal, setProposal] = useState(null);
  const [childId, setChildId] = useState(null);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [files, setFiles] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const expectedBeforeCreate = useMemo(() => {
    if (isSingle) {
      return [kind === 'PROGRESS' ? 'PROGRESS_REPORT' : 'FINAL_REPORT'];
    }
    return kind === 'EXTENSION'
      ? ['EXT_REQUEST_LETTER','EXT_PROGRESS_REPORT_LAO','EXT_PROGRESS_REPORT_ENG','EXT_CURRENT_APPROVAL','EXT_FEE']
      : ['AMD_REQUEST_LETTER','AMD_REVISED_DOCS_LAO','AMD_REVISED_DOCS_ENG','AMD_CURRENT_APPROVAL','AMD_FEE'];
  }, [kind, isSingle]);

  useEffect(() => {
    if (!UUID.test(proposalId)) {
      setError('Missing or invalid proposal id. Open this page from a Proposal.');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/proposals/details/${proposalId}`, { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        setProposal(data.proposal);
      } catch {
        setError('Failed to load proposal details');
      }
    })();
  }, [proposalId, authHeaders]);

  const handleFile = (key, file) =>
    setFiles(prev => ({ ...prev, [key]: file || null }));

  const mustSelectDocs = requiredDocs.length ? requiredDocs : expectedBeforeCreate;
  const allChosen = mustSelectDocs.length > 0 && mustSelectDocs.every(k => files[k] instanceof File);

  const canSubmit =
    UUID.test(proposalId) &&
    proposal?.status === 'Approved' &&
    allChosen &&
    !creating;

  async function createAndUpload() {
    setError('');
    setCreating(true);
    try {
      if (!UUID.test(proposalId)) throw new Error('Invalid proposal id');

      const createRes = await fetch(`${API}/proposals/${proposalId}/children`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ kind }),
      });
      const createJson = await createRes.json();
      if (!createRes.ok) throw new Error(createJson?.error || `Create failed (HTTP ${createRes.status})`);

      const { childId: newChildId, requiredDocs: serverRequired } = createJson;
      setChildId(newChildId);
      setRequiredDocs(serverRequired || []);

      for (const docKey of (serverRequired || mustSelectDocs)) {
        const f = files[docKey];
        if (!(f instanceof File)) throw new Error(`Please select: ${DOC_LABELS[docKey] || docKey}`);

        const fd = new FormData();
        fd.append('doc_type', docKey);
        fd.append('file', f);
        fd.append('document_name', DOC_LABELS[docKey] || docKey);

        const upRes = await fetch(`${API}/children/${newChildId}/documents`, {
          method: 'POST',
          headers: authHeaders,
          body: fd,
        });
        const upJson = await upRes.json();
        if (!upRes.ok) throw new Error(upJson?.error || `Upload failed for ${docKey} (HTTP ${upRes.status})`);
      }

      navigate(`/children/${newChildId}`);
    } catch (e) {
      setError(e.message || 'Failed to create request');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="proposal-details-container wizard">
      <div className="card">
        <div className="details-header">
          <h2 style={{ margin: 0 }}>
            {kind === 'EXTENSION' && 'New Extension Request'}
            {kind === 'AMENDMENT' && 'New Amendment Request'}
            {kind === 'PROGRESS' && 'Submit Progress Report'}
            {kind === 'FINAL' && 'Submit Final Report'}
          </h2>
        </div>

        {proposal && (
          <div className="details-grid" style={{ marginTop: 6 }}>
            <div>
              <div className="muted">Proposal</div>
              <div className="value">{proposal.title}</div>
            </div>
            <div>
              <div className="muted">ID</div>
              <div className="value">{proposal.custom_id || proposal.proposal_id}</div>
            </div>
            <div>
              <div className="muted">Status</div>
              <div className="value">{proposal.status}</div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="section-title">Required Documents</div>

        {(requiredDocs.length ? requiredDocs : expectedBeforeCreate).map(dt => (
          <div key={dt} className="doc-row">
            <div className="doc-label">{DOC_LABELS[dt] || dt}</div>
            <div className="doc-inputs">
              <input
                className="file-input"
                type="file"
                disabled={creating}
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={e => handleFile(dt, e.target.files?.[0] || null)}
              />
              {files[dt] && <div className="filename muted">{files[dt].name}</div>}
            </div>
          </div>
        ))}

        {!proposal || proposal.status !== 'Approved' ? (
          <div className="error-text" style={{ marginTop: 8 }}>
            Only <strong>Approved</strong> proposals can submit these requests.
          </div>
        ) : null}
        {error && <div className="error-text" style={{ marginTop: 8 }}>{error}</div>}
      </div>

      <div className="card">
        <div className="btn-row right">
          <button className="btn ghost" type="button" onClick={() => navigate(-1)} disabled={creating}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!canSubmit}
            onClick={createAndUpload}
            title={!canSubmit ? 'Choose all required files first' : 'Create request and upload files'}
          >
            {creating ? 'Submitting…' : 'Create Request & Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}
